import React, { useState, useEffect, useCallback } from "react";
import {
    Settings,
    Shield,
    Server,
    Eye,
    EyeOff,
    CheckCircle,
    XCircle,
    RefreshCw,
    Copy,
    Loader2,
    Clock,
    Users,
    ArrowRightLeft,
    AlertTriangle,
    History,
    Wand2,
} from "lucide-react";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { PageLoader } from "../shared/LoadingSpinner";
import { toast } from "sonner";
import { formatDateTime } from "../../lib/utils";

// Password field with show/hide toggle
function SecretField({ value, onChange, placeholder, testId }) {
    const [visible, setVisible] = useState(false);
    return (
        <div className="relative">
            <Input
                type={visible ? "text" : "password"}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                data-testid={testId}
                className="pr-10"
            />
            <button
                type="button"
                onClick={() => setVisible(!visible)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
            >
                {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
        </div>
    );
}

// Read-only field with copy button
function CopyField({ value, label }) {
    const handleCopy = () => {
        navigator.clipboard.writeText(value);
        toast.success("Copie dans le presse-papier");
    };
    return (
        <div>
            {label && <Label className="form-label">{label}</Label>}
            <div className="flex items-center gap-2">
                <Input value={value} readOnly className="bg-gray-50 text-gray-500 font-mono text-xs" />
                <Button variant="outline" size="sm" onClick={handleCopy} data-testid="copy-redirect-uri">
                    <Copy className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
}

// ============ ENTRA ID SECTION ============
function EntraIdSection() {
    const [form, setForm] = useState({
        tenant_id: "",
        client_id: "",
        client_secret: "",
        scopes: "openid profile email offline_access",
        sso_enabled: false,
    });
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const redirectUri = `${window.location.origin}/api/auth/sso/callback`;

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const data = await api.getIntegrationSettings();
            if (data.entra_id && Object.keys(data.entra_id).length > 0) {
                setForm((prev) => ({ ...prev, ...data.entra_id }));
            }
        } catch (err) {
            console.error("Failed to load Entra ID settings:", err);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const result = await api.testEntraConnection();
            setTestResult(result);
            if (result.success) {
                toast.success(result.message);
            } else {
                toast.error(result.message);
            }
        } catch (err) {
            toast.error("Erreur lors du test de connexion");
            setTestResult({ success: false, message: err.response?.data?.detail || "Erreur" });
        } finally {
            setTesting(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.saveEntraSettings({
                ...form,
                redirect_uri: redirectUri,
            });
            toast.success("Configuration Entra ID sauvegardee");
            setTestResult(null);
        } catch (err) {
            toast.error("Erreur lors de la sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    return (
        <div className="card" data-testid="entra-id-section">
            <div className="card-header flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                    <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                    <h2 className="text-sm font-semibold">Microsoft Entra ID (SSO)</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Authentification unique via Azure Active Directory
                    </p>
                </div>
            </div>
            <div className="card-body space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="form-label">Tenant ID *</Label>
                        <Input
                            value={form.tenant_id}
                            onChange={(e) => updateField("tenant_id", e.target.value)}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            data-testid="entra-tenant-id"
                        />
                    </div>
                    <div>
                        <Label className="form-label">Client ID *</Label>
                        <Input
                            value={form.client_id}
                            onChange={(e) => updateField("client_id", e.target.value)}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            data-testid="entra-client-id"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="form-label">Client Secret *</Label>
                        <SecretField
                            value={form.client_secret}
                            onChange={(e) => updateField("client_secret", e.target.value)}
                            placeholder="Votre client secret"
                            testId="entra-client-secret"
                        />
                    </div>
                    <CopyField value={redirectUri} label="Redirect URI (a copier dans Azure)" />
                </div>

                <div>
                    <Label className="form-label">Scopes</Label>
                    <Input
                        value={form.scopes}
                        onChange={(e) => updateField("scopes", e.target.value)}
                        data-testid="entra-scopes"
                        className="font-mono text-sm"
                    />
                </div>

                <div className="flex items-center gap-3 pt-2">
                    <Switch
                        checked={form.sso_enabled}
                        onCheckedChange={(v) => updateField("sso_enabled", v)}
                        data-testid="entra-sso-toggle"
                    />
                    <Label className="text-sm font-medium cursor-pointer">SSO active</Label>
                </div>

                {/* Test result */}
                {testResult && (
                    <div className={`flex items-start gap-3 p-3 rounded-lg border text-sm ${
                        testResult.success
                            ? "bg-green-50 border-green-200 text-green-800"
                            : "bg-red-50 border-red-200 text-red-800"
                    }`}>
                        {testResult.success ? (
                            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        ) : (
                            <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        )}
                        <div>
                            <p className="font-medium">{testResult.message}</p>
                            {testResult.issuer && (
                                <p className="text-xs mt-1 opacity-80 font-mono">{testResult.issuer}</p>
                            )}
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t">
                    <Button
                        variant="outline"
                        onClick={handleTest}
                        disabled={testing || !form.tenant_id}
                        data-testid="test-entra-btn"
                    >
                        {testing ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Tester la connexion
                    </Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="save-entra-btn">
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Sauvegarder
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ============ LDAP SECTION ============
function LdapSection() {
    const { refreshUsers } = useApp();
    const [form, setForm] = useState({
        host: "",
        port: 389,
        base_dn: "",
        bind_dn: "",
        bind_password: "",
        user_filter: "(&(objectClass=user)(objectCategory=person)(!(userAccountControl:1.2.840.113556.1.4.803:=2)))",
        attribute_mappings: {
            givenName: "first_name",
            sn: "last_name",
            mail: "email",
            department: "department_name",
            sAMAccountName: "username",
            distinguishedName: "dn",
            manager: "manager_dn",
        },
        auto_sync_enabled: false,
        sync_frequency: "24h",
        ldap_enabled: false,
    });
    const [testing, setTesting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [syncResult, setSyncResult] = useState(null);
    const [lastSync, setLastSync] = useState(null);
    const [syncLogs, setSyncLogs] = useState([]);
    const [showLogs, setShowLogs] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const [settingsData, logsData] = await Promise.all([
                api.getIntegrationSettings(),
                api.getLdapSyncLogs(5),
            ]);
            if (settingsData.ldap && Object.keys(settingsData.ldap).length > 0) {
                const ldap = settingsData.ldap;
                setForm((prev) => ({
                    ...prev,
                    host: ldap.host || "",
                    port: ldap.port || 389,
                    base_dn: ldap.base_dn || "",
                    bind_dn: ldap.bind_dn || "",
                    bind_password: ldap.bind_password || "",
                    user_filter: ldap.user_filter || prev.user_filter,
                    attribute_mappings: ldap.attribute_mappings || prev.attribute_mappings,
                    auto_sync_enabled: ldap.auto_sync_enabled || false,
                    sync_frequency: ldap.sync_frequency || "24h",
                    ldap_enabled: ldap.ldap_enabled || false,
                }));
                if (ldap.last_sync) {
                    setLastSync(ldap.last_sync);
                }
            }
            setSyncLogs(logsData);
        } catch (err) {
            console.error("Failed to load LDAP settings:", err);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const result = await api.testLdapConnectionV2();
            setTestResult(result);
            if (result.success) toast.success(result.message);
            else toast.error(result.message);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors du test");
            setTestResult({ success: false, message: err.response?.data?.detail || "Erreur" });
        } finally {
            setTesting(false);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            const result = await api.triggerLdapSyncV2();
            setSyncResult(result.results);
            toast.success(result.message);
            refreshUsers();
            // Refresh logs
            const logsData = await api.getLdapSyncLogs(5);
            setSyncLogs(logsData);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la synchronisation");
        } finally {
            setSyncing(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.saveLdapSettings(form);
            toast.success("Configuration LDAP sauvegardee");
        } catch (err) {
            toast.error("Erreur lors de la sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const updateMapping = (key, value) =>
        setForm((prev) => ({
            ...prev,
            attribute_mappings: { ...prev.attribute_mappings, [key]: value },
        }));

    return (
        <div className="space-y-6" data-testid="ldap-section">
            {/* Connection */}
            <div className="card">
                <div className="card-header flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                        <Server className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold">Active Directory / LDAP</h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Synchronisation des utilisateurs depuis l'annuaire
                        </p>
                    </div>
                </div>
                <div className="card-body space-y-5">
                    {/* LDAP Enabled Toggle */}
                    <div className="flex items-center gap-3">
                        <Switch
                            checked={form.ldap_enabled}
                            onCheckedChange={(v) => updateField("ldap_enabled", v)}
                            data-testid="ldap-enabled-toggle"
                        />
                        <Label className="text-sm font-medium cursor-pointer">LDAP actif</Label>
                    </div>

                    {!form.ldap_enabled && (
                        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="ldap-disabled-banner">
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <p>La connexion LDAP est désactivée. Les utilisateurs ne seront pas synchronisés depuis Active Directory.</p>
                        </div>
                    )}

                    {/* Connection params */}
                    <fieldset disabled={!form.ldap_enabled} className={!form.ldap_enabled ? "opacity-50" : ""}>
                    <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <Label className="form-label">Hote LDAP *</Label>
                            <Input
                                value={form.host}
                                onChange={(e) => updateField("host", e.target.value)}
                                placeholder="ldap://192.168.1.10 ou ldaps://dc.domain.local"
                                data-testid="ldap-host"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Port</Label>
                            <Input
                                type="number"
                                value={form.port}
                                onChange={(e) => updateField("port", parseInt(e.target.value) || 389)}
                                data-testid="ldap-port"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="form-label">Base DN *</Label>
                        <Input
                            value={form.base_dn}
                            onChange={(e) => updateField("base_dn", e.target.value)}
                            placeholder="DC=ad1,DC=entreprise,DC=com"
                            data-testid="ldap-base-dn-v2"
                            className="font-mono text-sm"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="form-label">Bind DN (compte de service)</Label>
                            <Input
                                value={form.bind_dn}
                                onChange={(e) => updateField("bind_dn", e.target.value)}
                                placeholder="CN=svc-ticketing,OU=ServiceAccounts,DC=ad1,DC=entreprise,DC=com"
                                data-testid="ldap-bind-dn-v2"
                                className="font-mono text-sm"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Bind Password</Label>
                            <SecretField
                                value={form.bind_password}
                                onChange={(e) => updateField("bind_password", e.target.value)}
                                placeholder="Mot de passe du compte de service"
                                testId="ldap-bind-password-v2"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="form-label">Filtre utilisateurs</Label>
                        <Input
                            value={form.user_filter}
                            onChange={(e) => updateField("user_filter", e.target.value)}
                            data-testid="ldap-user-filter"
                            className="font-mono text-xs"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            Filtre LDAP pour exclure les comptes desactives
                        </p>
                    </div>

                    {/* Test result */}
                    </div>
                    </fieldset>
                    {testResult && (
                        <div className={`flex items-start gap-3 p-3 rounded-lg border text-sm ${
                            testResult.success
                                ? "bg-green-50 border-green-200 text-green-800"
                                : "bg-red-50 border-red-200 text-red-800"
                        }`}>
                            {testResult.success ? (
                                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            ) : (
                                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            )}
                            <p className="font-medium">{testResult.message}</p>
                        </div>
                    )}

                    <div className="flex items-center gap-3 pt-2 border-t">
                        <Button
                            variant="outline"
                            onClick={handleTest}
                            disabled={testing || !form.host || !form.ldap_enabled}
                            data-testid="test-ldap-v2-btn"
                        >
                            {testing ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Tester la connexion LDAP
                        </Button>
                    </div>
                </div>
            </div>

            {/* Attribute Mappings */}
            <div className="card">
                <div className="card-header flex items-center gap-3">
                    <ArrowRightLeft className="w-4 h-4 text-gray-500" />
                    <h2 className="text-sm font-semibold">Attributs mappes</h2>
                </div>
                <div className="card-body">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 pb-2">
                            Attribut LDAP
                        </div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 pb-2">
                            Champ systeme
                        </div>
                        {Object.entries(form.attribute_mappings).map(([ldapAttr, sysField]) => (
                            <React.Fragment key={ldapAttr}>
                                <div className="flex items-center">
                                    <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                                        {ldapAttr}
                                    </code>
                                </div>
                                <Input
                                    value={sysField}
                                    onChange={(e) => updateMapping(ldapAttr, e.target.value)}
                                    className="text-sm font-mono"
                                    data-testid={`mapping-${ldapAttr}`}
                                />
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>

            {/* Sync Settings */}
            <div className="card">
                <div className="card-header flex items-center gap-3">
                    <RefreshCw className="w-4 h-4 text-gray-500" />
                    <h2 className="text-sm font-semibold">Synchronisation</h2>
                </div>
                <div className="card-body space-y-5">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-3">
                            <Switch
                                checked={form.auto_sync_enabled}
                                onCheckedChange={(v) => updateField("auto_sync_enabled", v)}
                                data-testid="ldap-auto-sync-toggle"
                            />
                            <Label className="text-sm font-medium">Synchronisation automatique</Label>
                        </div>
                        {form.auto_sync_enabled && (
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <Select
                                    value={form.sync_frequency}
                                    onValueChange={(v) => updateField("sync_frequency", v)}
                                >
                                    <SelectTrigger className="w-44" data-testid="ldap-sync-frequency">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1h">Toutes les heures</SelectItem>
                                        <SelectItem value="6h">Toutes les 6 heures</SelectItem>
                                        <SelectItem value="12h">Toutes les 12 heures</SelectItem>
                                        <SelectItem value="24h">Toutes les 24 heures</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    {/* Last sync info */}
                    {syncLogs.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    Derniere synchronisation
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowLogs(!showLogs)}
                                    className="text-xs"
                                >
                                    <History className="w-3 h-3 mr-1" />
                                    {showLogs ? "Masquer" : "Historique"}
                                </Button>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                                <span className="text-gray-600">
                                    {formatDateTime(syncLogs[0].timestamp)}
                                </span>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                    syncLogs[0].status === "success"
                                        ? "bg-green-50 text-green-700 border border-green-200"
                                        : "bg-orange-50 text-orange-700 border border-orange-200"
                                }`}>
                                    {syncLogs[0].status === "success" ? (
                                        <CheckCircle className="w-3 h-3" />
                                    ) : (
                                        <AlertTriangle className="w-3 h-3" />
                                    )}
                                    {syncLogs[0].status}
                                </span>
                                <span className="text-gray-500">
                                    {syncLogs[0].users_created} crees,{" "}
                                    {syncLogs[0].users_updated} maj,{" "}
                                    {syncLogs[0].users_merged} fusionnes,{" "}
                                    {syncLogs[0].users_disabled} desactives
                                </span>
                            </div>

                            {/* Sync log history */}
                            {showLogs && syncLogs.length > 1 && (
                                <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                                    {syncLogs.slice(1).map((log, idx) => (
                                        <div key={log.timestamp || idx} className="flex items-center gap-4 text-xs text-gray-500">
                                            <span>{formatDateTime(log.timestamp)}</span>
                                            <span className={log.status === "success" ? "text-green-600" : "text-orange-600"}>
                                                {log.status}
                                            </span>
                                            <span>
                                                +{log.users_created} / ~{log.users_updated} / ={log.users_merged} / -{log.users_disabled}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Sync results */}
                    {syncResult && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                            <div className="flex items-center gap-2 font-medium mb-2">
                                <Users className="w-4 h-4" />
                                Resultats de la synchronisation
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="bg-white rounded p-2 text-center border border-blue-100">
                                    <div className="text-lg font-bold text-green-600">{syncResult.users_created}</div>
                                    <div className="text-xs text-gray-500">Crees</div>
                                </div>
                                <div className="bg-white rounded p-2 text-center border border-blue-100">
                                    <div className="text-lg font-bold text-blue-600">{syncResult.users_updated}</div>
                                    <div className="text-xs text-gray-500">Mis a jour</div>
                                </div>
                                <div className="bg-white rounded p-2 text-center border border-blue-100">
                                    <div className="text-lg font-bold text-purple-600">{syncResult.users_merged}</div>
                                    <div className="text-xs text-gray-500">Fusionnes</div>
                                </div>
                                <div className="bg-white rounded p-2 text-center border border-blue-100">
                                    <div className="text-lg font-bold text-orange-600">{syncResult.users_disabled}</div>
                                    <div className="text-xs text-gray-500">Desactives</div>
                                </div>
                            </div>
                            {syncResult.errors?.length > 0 && (
                                <div className="mt-2 text-xs text-red-600">
                                    {syncResult.errors.map((e, i) => (
                                        <p key={`err-${i}-${e.slice(0,20)}`}>{e}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t">
                        <Button
                            variant="outline"
                            onClick={handleSync}
                            disabled={syncing || !form.ldap_enabled}
                            data-testid="trigger-ldap-sync-v2-btn"
                        >
                            {syncing ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Synchroniser maintenant
                        </Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-ldap-btn">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Sauvegarder
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============ MAIN PAGE ============
// ==================== AI SECTION ====================
function AiSection() {
    const [config, setConfig] = useState({ type: "openai", api_key: "", model: "gpt-4o-mini", provider: "openai", ai_enabled: false });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showKey, setShowKey] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);

    const loadConfig = useCallback(async () => {
        try {
            const data = await api.getAiConfig();
            setConfig(data);
        } catch { /* config not set yet */ }
        setLoading(false);
    }, []);

    useEffect(() => { loadConfig(); }, [loadConfig]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = await api.saveAiConfig(config);
            setConfig(data);
            toast.success("Configuration IA sauvegardée");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la sauvegarde");
        }
        setSaving(false);
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const result = await api.reformulateDescription({
                description: "mon ordi marche plus il s'éteint tout seul depuis ce matin j'ai essayé de le rallumer mais rien à faire. c'est un dell latitude je crois. merci de m'aider svp",
                ticket_type: "incident",
                title: "PC en panne",
            });
            setTestResult({ success: true, text: result.reformulated, model: result.model });
        } catch (err) {
            setTestResult({ success: false, text: err.response?.data?.detail || "Erreur lors du test" });
        }
        setTesting(false);
    };

    if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

    return (
        <div className="space-y-6" data-testid="ai-config-section">
            {/* Toggle */}
            <div className="card">
                <div className="card-body">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Assistant IA</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Reformulation automatique des descriptions de tickets par l'IA</p>
                        </div>
                        <Switch
                            checked={config.ai_enabled}
                            onCheckedChange={(v) => setConfig({ ...config, ai_enabled: v })}
                            data-testid="ai-enabled-toggle"
                        />
                    </div>
                    {!config.ai_enabled && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-xs text-amber-800">L'assistant IA est désactivé. Les agents n'auront pas accès à la reformulation automatique des descriptions.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Config */}
            <div className={`card ${!config.ai_enabled ? "opacity-60 pointer-events-none" : ""}`}>
                <div className="card-header">
                    <h3 className="text-sm font-semibold text-gray-900">Configuration OpenAI</h3>
                </div>
                <div className="card-body space-y-4">
                    <div>
                        <Label className="form-label">Clé API OpenAI</Label>
                        <div className="relative">
                            <Input
                                type={showKey ? "text" : "password"}
                                value={config.api_key}
                                onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
                                placeholder="sk-..."
                                className="pr-10"
                                data-testid="ai-api-key"
                            />
                            <button
                                type="button"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                onClick={() => setShowKey(!showKey)}
                            >
                                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <Label className="form-label">Modèle</Label>
                        <Input
                            value={config.model}
                            onChange={(e) => setConfig({ ...config, model: e.target.value })}
                            placeholder="gpt-4o-mini"
                            data-testid="ai-model"
                        />
                        <p className="text-xs text-gray-400 mt-1">Modèles recommandés : gpt-4o-mini (rapide et économique), gpt-4o (plus précis)</p>
                    </div>
                    <div>
                        <Label className="form-label">Provider</Label>
                        <Select value={config.provider || "openai"} onValueChange={(v) => setConfig({ ...config, provider: v })}>
                            <SelectTrigger data-testid="ai-provider">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="openai">OpenAI</SelectItem>
                                <SelectItem value="anthropic">Anthropic</SelectItem>
                                <SelectItem value="google">Google</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                        <Button onClick={handleTest} variant="outline" disabled={testing || !config.api_key || config.api_key === "********"} data-testid="ai-test-btn">
                            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
                            Tester
                        </Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="ai-save-btn">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Sauvegarder
                        </Button>
                    </div>

                    {testResult && (
                        <div className={`mt-3 p-4 rounded-lg border ${testResult.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="ai-test-result">
                            <div className="flex items-center gap-2 mb-2">
                                {testResult.success ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                                <span className={`text-xs font-semibold ${testResult.success ? "text-green-700" : "text-red-700"}`}>
                                    {testResult.success ? `Test réussi (${testResult.model})` : "Test échoué"}
                                </span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{testResult.text}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function AdminIntegrations() {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Brief loading state for consistency
        const timer = setTimeout(() => setLoading(false), 300);
        return () => clearTimeout(timer);
    }, []);

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-integrations">
            <div className="page-header">
                <h1 className="page-title">Integrations</h1>
                <p className="page-subtitle">
                    Configuration des connexions Microsoft Entra ID et Active Directory / LDAP
                </p>
            </div>

            <Tabs defaultValue="entra" className="mt-6">
                <TabsList className="mb-6">
                    <TabsTrigger value="entra" className="gap-2" data-testid="tab-entra">
                        <Shield className="w-4 h-4" />
                        Microsoft Entra ID
                    </TabsTrigger>
                    <TabsTrigger value="ldap" className="gap-2" data-testid="tab-ldap">
                        <Server className="w-4 h-4" />
                        Active Directory / LDAP
                    </TabsTrigger>
                    <TabsTrigger value="ai" className="gap-2" data-testid="tab-ai">
                        <Wand2 className="w-4 h-4" />
                        IA / OpenAI
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="entra">
                    <EntraIdSection />
                </TabsContent>

                <TabsContent value="ldap">
                    <LdapSection />
                </TabsContent>

                <TabsContent value="ai">
                    <AiSection />
                </TabsContent>
            </Tabs>
        </div>
    );
}
